// VoiceMicButton.tsx — Pedagogically-aware microphone toggle
//
// The mic button is a pedagogical signal. Its size, glow, and
// visibility communicate "now it's your turn to articulate."
// Design from architecture doc:
//   - Introduction: no button (hidden)
//   - Samtale: 🎤 32px, appears after Anna's first question
//   - Eksamen: 🎤 36px with glow, always visible

import { useTranslation } from 'react-i18next';
import type { VoiceAvailabilityState } from '@hooks/useVoiceAvailability';
import type { VoiceInputState } from '@hooks/useVoiceInput';

interface VoiceMicButtonProps {
  availability: VoiceAvailabilityState;
  voiceState: VoiceInputState;
  audioLevel: number;
  isStreaming: boolean; // Anna is currently responding
  onToggle: () => void;
  /** Error message from voice input */
  voiceError?: string | null;
}

const SIZE_MAP = {
  sm: { button: 'w-8 h-8', icon: 'w-4 h-4' },
  md: { button: 'w-10 h-10', icon: 'w-5 h-5' },
  lg: { button: 'w-12 h-12', icon: 'w-6 h-6' },
};

export default function VoiceMicButton({
  availability,
  voiceState,
  audioLevel,
  isStreaming,
  onToggle,
  voiceError,
}: VoiceMicButtonProps) {
  const { t } = useTranslation('session');

  if (availability.visibility === 'hidden') return null;

  const isListening = voiceState === 'listening';
  const isError = voiceState === 'error';
  const isDisabled = isStreaming || voiceState === 'requesting-mic' || voiceState === 'processing';
  const size = SIZE_MAP[availability.buttonSize];

  // Dynamic ring scale based on audio level when listening
  const ringScale = isListening ? 1 + audioLevel * 0.4 : 1;

  return (
    <div className="relative flex items-center justify-center">
      {/* Audio level ring (visible when listening) */}
      {isListening && (
        <div
          className="absolute rounded-full bg-green-500/20 transition-transform duration-75"
          style={{
            width: '150%',
            height: '150%',
            transform: `scale(${ringScale})`,
          }}
        />
      )}

      {/* Glow effect (exam mode / high Bloom) */}
      {availability.showGlow && !isListening && (
        <div
          className="absolute rounded-full bg-primary-500/20 animate-pulse"
          style={{ width: '130%', height: '130%' }}
        />
      )}

      <button
        onClick={onToggle}
        disabled={isDisabled}
        className={`
          relative ${size.button} rounded-full flex items-center justify-center
          transition-all duration-200
          ${isListening
            ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
            : isError
              ? 'bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-800/40 text-amber-700 dark:text-amber-400 ring-2 ring-amber-500/50'
              : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
          }
          ${availability.visibility === 'prominent' && !isListening && !isError
            ? 'ring-2 ring-primary-500/50'
            : ''
          }
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-label={isError
          ? t('voice.micError', 'Microphone error — click to retry')
          : isListening
            ? t('voice.stopRecording', 'Stop recording')
            : t('voice.startRecording', 'Start recording')
        }
        title={isError
          ? voiceError
            ? t(`voice.errors.${voiceError}`, t('voice.micErrorHint'))
            : t('voice.micErrorHint')
          : availability.hintKey ? t(availability.hintKey) : undefined
        }
      >
        {/* Mic icon */}
        <svg
          className={size.icon}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          {isListening ? (
            // Stop icon (square)
            <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
          ) : (
            // Mic icon
            <>
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </>
          )}
        </svg>
      </button>

      {/* Listening indicator dot */}
      {isListening && (
        <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
      )}

      {/* Error indicator */}
      {isError && (
        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-500 text-white flex items-center justify-center text-[8px] font-bold">
          !
        </div>
      )}
    </div>
  );
}
